"use client";

import { usePathname, useRouter } from "next/navigation";
import { useUser } from "@/zustand-hooks/user-hook";
import { useTranslations } from "next-intl";
import { Lock, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const moodStyles = {
    info: {
        card: "border-blue-500 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-600",
        title: "text-blue-900 dark:text-blue-100",
        description: "text-blue-800 dark:text-blue-200",
        icon: "text-blue-700 dark:text-blue-300",
        button: "!bg-blue-600 hover:!bg-blue-700 !text-white border-2 border-blue-700 shadow-lg",
    },
    warning: {
        card: "border-amber-500 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-600",
        title: "text-amber-900 dark:text-amber-100",
        description: "text-amber-800 dark:text-amber-200",
        icon: "text-amber-700 dark:text-amber-300",
        button: "!bg-amber-600 hover:!bg-amber-700 !text-white border-2 border-amber-700 shadow-lg",
    },
    threat: {
        card: "border-red-500 bg-red-50 dark:bg-red-950/30 dark:border-red-600",
        title: "text-red-900 dark:text-red-100",
        description: "text-red-800 dark:text-red-200",
        icon: "text-red-700 dark:text-red-300",
        button: "!bg-red-600 hover:!bg-red-700 !text-white border-2 border-red-700 shadow-lg",
    },
} as const;

export function BlockedAccountProvider({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();
    const router = useRouter();
    const user = useUser((state) => state.user);
    const t = useTranslations("pages.Login.blocked");

    const isBlocked = !!user?.account_frozen;
    const chatAllowed = !!user?.block_chat_support_enabled;
    const isOnChatsPage = pathname?.includes("/chats") ?? false;

    if (isBlocked && !isOnChatsPage) {
        const mood = user?.block_message_mood === "info" || user?.block_message_mood === "warning" || user?.block_message_mood === "threat"
            ? user.block_message_mood
            : "warning";
        const title = user?.block_message_title || t("default_title");
        const description = user?.block_message_description || t("default_description");

        return (
            <div className="flex-1 flex items-center justify-center">
                <div
                    className={cn(
                        "w-full max-w-md rounded-xl border-2 p-6 shadow-lg",
                        moodStyles[mood].card
                    )}
                >
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-lg bg-white/90 dark:bg-slate-800/80">
                            <Lock className={cn("h-8 w-8", moodStyles[mood].icon)} />
                        </div>
                        <h1 className={cn("text-xl font-bold", moodStyles[mood].title)}>
                            {title}
                        </h1>
                    </div>
                    <p className={cn("whitespace-pre-wrap mb-6", moodStyles[mood].description)}>
                        {description}
                    </p>
                    {chatAllowed && (
                        <Button
                            onClick={() => router.push("/chats")}
                            className={cn("gap-2 px-5 py-2.5", moodStyles[mood].button)}
                        >
                            <MessageCircle className="h-4 w-4 shrink-0" />
                            {t("contact_support")}
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    return <>{children}</>;
}
